/**
 * Utility functions for Login component
 */

/**
 * Checks and formats error messages for display
 * @param {string|object} userStateError - The error state from user slice
 * @returns {string} Formatted error message
 */
export function checkError(userStateError) {
    if (userStateError === "http_error_401") {
        return ""
    } else if (typeof userStateError === 'object' && userStateError !== null) {
        return "Error: " + JSON.stringify(userStateError)
    } else {
        return "Error: " + userStateError.toString()
    }
}

/**
 * Handles Enter key press for login form
 * @param {Event} e - Keyboard event
 * @param {Function} doLogin - Login function to call
 */
export function doLoginKey(e, doLogin) {
    if (e.key === "Enter") {
        doLogin()
    }
}

/**
 * Validates login form inputs
 * @param {Object} loginForm - Form data with username and password
 * @returns {Object} Validation state with error flags and messages
 */
export function validateForm(loginForm, t = (key) => key) {
    return {
        'invalid_username': loginForm.username === '',
        'invalid_password': loginForm.password === '',
        invalidText_username: loginForm.username === '' ? t('auth.usernameRequired') : '',
        invalidText_password: loginForm.password === '' ? t('auth.passwordRequired') : '',
    }
}

/**
 * Handles input changes and updates validation state
 * @param {Event} e - Input change event
 * @param {Object} loginForm - Current form data
 * @param {Object} invalid_dictionary - Current validation state
 * @returns {Object} Updated form data and validation state
 */
export function handleInputChange(e, loginForm, invalid_dictionary, t = (key) => key) {
    const newFormData = { ...loginForm, [e.target.name]: e.target.value }
    
    const validationState = {
        'invalid_username': e.target.name === "username" ? e.target.value === '' : false,
        'invalid_password': e.target.name === "password" ? e.target.value === '' : false,
        invalidText_username: e.target.name === "username" && e.target.value === '' ? t('auth.usernameRequired') : '',
        invalidText_password: e.target.name === "password" && e.target.value === '' ? t('auth.passwordRequired') : '',
    }

    return {
        formData: newFormData,
        validationState: {
            ...invalid_dictionary,
            ...validationState
        }
    }
}

/**
 * Determines error state based on HTTP status code
 * @param {number} httpCode - HTTP status code
 * @returns {Object} Error validation state
 */
export function getErrorState(httpCode, t = (key) => key) {
    const isClientErr = String(httpCode).startsWith('4');
    
    return isClientErr
        ? {
            invalid_username: true,
            invalid_password: true,
            invalidText_username: t('auth.invalidCredentials'),
            invalidText_password: t('auth.invalidCredentials'),
        }
        : {
            invalid_username: false,
            invalid_password: false,
            invalidText_username: '',
            invalidText_password: '',
        };
}

/**
 * Validates login form and updates validation state
 * @param {Object} loginForm - Form data with username and password
 * @param {Object} invalid_dictionary - Current validation state
 * @returns {Object} Updated validation state
 */
export function validateLogin(loginForm, invalid_dictionary, t) {
    const validationState = validateForm(loginForm, t);
    return {
        ...invalid_dictionary, 
        ...validationState
    }
}

/**
 * Handles input changes for login form
 * @param {Event} e - Input change event
 * @param {Object} loginForm - Current form data
 * @param {Object} invalid_dictionary - Current validation state
 * @returns {Object} Updated form data and validation state
 */
export function handleLoginInputs(e, loginForm, invalid_dictionary, t) {
    const { formData, validationState } = handleInputChange(e, loginForm, invalid_dictionary, t);
    return { formData, validationState };
}
